import { useState, type FormEvent } from "react";

import { Logo, useAuth } from "../components/Auth";
import { api, type User } from "../lib/api";

/** The sign-in screen. Also handles the first sign-in after an administrator
 *  creates or resets an account, where a new password must be set before the
 *  app opens. */
export function Login() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form className="signin-card" onSubmit={submit}>
        <Logo className="signin-logo" />
        <h1>Stock &amp; sales</h1>
        <p className="muted">Brinley &amp; Company operations</p>
        {error && <p className="signin-error" role="alert">{error}</p>}
        <label className="field">
          <span className="field-label">Email</span>
          <input type="email" name="email" autoComplete="username" autoFocus required
                 aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Password</span>
          <input type="password" name="password" autoComplete="current-password" required
                 aria-label="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="signin-foot muted">No account? An administrator creates one for you.</p>
      </form>
    </div>
  );
}

export function ForcePasswordChange() {
  const { refresh, signOut } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== again) { setError("The two new passwords do not match."); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post<{ user: User }>("/auth/password", { current_password: current, new_password: next });
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form className="signin-card" onSubmit={submit}>
        <Logo className="signin-logo" />
        <h1>Choose a password</h1>
        <p className="muted">Your account was set up with a temporary password. Pick your own to continue.</p>
        {error && <p className="signin-error" role="alert">{error}</p>}
        <label className="field">
          <span className="field-label">Temporary password</span>
          <input type="password" autoComplete="current-password" required aria-label="Temporary password"
                 value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">New password</span>
          <input type="password" autoComplete="new-password" required aria-label="New password"
                 value={next} onChange={(e) => setNext(e.target.value)} />
          <span className="field-hint">At least 12 characters. A short phrase you will remember beats a clever one you will not.</span>
        </label>
        <label className="field">
          <span className="field-label">New password again</span>
          <input type="password" autoComplete="new-password" required aria-label="New password again"
                 value={again} onChange={(e) => setAgain(e.target.value)} />
        </label>
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>{busy ? "Saving…" : "Save and continue"}</button>
        <button className="link-btn signin-foot" type="button" onClick={() => void signOut()}>Sign out instead</button>
      </form>
    </div>
  );
}
